import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Badge, Table, Button, Popover, OverlayTrigger } from 'react-bootstrap';
import { store } from 'react-notifications-component';
import { TokenPaymentsServices } from '../../pages/home/TokenPayments/TokenPayments.service';
import { formatAmount, formatDateTime } from '../../../_metronic/utils/utils';
import * as constants from '../../constants';

const STEPS = ['Token Generated', 'Token Paid', 'Bank Details Added', 'Token Amount Transferred', 'Refunded'];

const getStepStatus = (stepId, statusId) => {
    if (statusId === 5) {                       // Refunded: steps 3 & 4 not applicable
        if (stepId === 3 || stepId === 4) return 'skipped';
        return stepId === 5 ? 'current' : 'done';
    }
    if (statusId === 4 && stepId === 5) return 'skipped'; // Transferred: refund not applicable
    if (stepId < statusId) return 'done';
    if (stepId === statusId) return 'current';
    return 'pending';
};

const getStepTheme = (status, isRefunded) => ({
    done: { bg: '#16a34a', border: '#16a34a', color: '#fff', label: '#166534', tag: 'Completed', tagBg: '#dcfce7', tagColor: '#166534' },
    current: isRefunded
        ? { bg: '#16a34a', border: '#16a34a', color: '#fff', label: '#166534', tag: 'Completed', tagBg: '#dcfce7', tagColor: '#166534' }
        : { bg: '#2563eb', border: '#2563eb', color: '#fff', label: '#1d4ed8', tag: 'Current', tagBg: '#dbeafe', tagColor: '#1d4ed8' },
    skipped: { bg: '#fef2f2', border: '#fca5a5', color: '#dc2626', label: '#b91c1c', tag: 'Not Applicable', tagBg: '#fee2e2', tagColor: '#b91c1c' },
    pending: { bg: '#fff', border: '#cbd5e1', color: '#94a3b8', label: '#94a3b8', tag: 'Pending', tagBg: '#f1f5f9', tagColor: '#64748b' },
}[status]);

const STATUS_COLORS = {
    0: { bg: '#f1f5f9', color: '#475569' },
    1: { bg: '#e0e7ff', color: '#3730a3' },
    2: { bg: '#fef3c7', color: '#92400e' },
    3: { bg: '#e0f2fe', color: '#075985' },
    4: { bg: '#dcfce7', color: '#166534' },
    5: { bg: '#fee2e2', color: '#991b1b' },
};

export const TokenStatusPill = ({ statusId, text }) => {
    const c = STATUS_COLORS[Number(statusId)] || STATUS_COLORS[0];
    return (
        <span style={{
            display: 'inline-block', padding: '4px 12px', borderRadius: '12px', whiteSpace: 'nowrap',
            fontSize: '12px', fontWeight: 600, background: c.bg, color: c.color
        }}>
            {text || '-'}
        </span>
    );
};

const show = (value) => (value === null || value === undefined || value === '' ? '-' : value);
const showDate = (value) => (value ? formatDateTime(value) : '-');


const renderViewValue = (title, value, id) => {
    if (!value) return '-';
    const popover = (
        <Popover id={`popover-${id}`} style={{ maxWidth: '360px' }}>
            <Popover.Title as="h6" className="mb-0" style={{ fontSize: '12px' }}>{title}</Popover.Title>
            <Popover.Content>
                <div className="d-flex align-items-center">
                    <code style={{ fontSize: '12px', color: '#1e293b', wordBreak: 'break-all', userSelect: 'all' }}>{value}</code>
                    <Button
                        variant="link"
                        size="sm"
                        className="ml-2 p-0"
                        title="Copy"
                        onClick={() => {
                            navigator.clipboard && navigator.clipboard.writeText(String(value));
                            store.addNotification({
                                message: `${title} copied`,
                                type: "success",
                                insert: "top",
                                container: "top-right",
                                dismiss: { duration: 1500 }
                            });
                        }}
                    >
                        <i className="flaticon2-copy"></i>
                    </Button>
                </div>
            </Popover.Content>
        </Popover>
    );
    return (
        <OverlayTrigger trigger="click" placement="top" rootClose overlay={popover}>
            <Button variant="link" size="sm" className="p-0" style={{ fontSize: '12px' }}>View</Button>
        </OverlayTrigger>
    );
};


const SECTIONS = [
    {
        title: 'Tenant Payment Details', key: 'tenantPayment', columns: [
            ['Payment Id', (r, idx) => renderViewValue('Payment ID', r.PaymentId, `pid-${idx}`)],
            ['Amount', r => formatAmount(r.Amount)],
            ['Payment Through', r => show(r.PaymentThrogh)],
            ['Payment Date', r => showDate(r.PaymentDate)],
            ['Processing Fee', r => formatAmount(r.ProcessingFee)],
            ['PG Ref ID', r => show(r.TransRefNum)],
            ['Payment Link', r => r.PaymentURL ? <a href={r.PaymentURL} target="_blank" rel="noopener noreferrer">Open</a> : '-'],
            ['Created By', r => show(r.CreatedBy)],
            ['Created On', r => show(r.CreatedOn)],
        ]
    },
    {
        title: 'Bank Details', key: 'bankDetails', columns: [
            ['Account Name', r => show(r.AccountName)],
            ['Account No', r => show(r.AccountNo)],
            ['IFSC Code', r => show(r.IfscCode)],
            ['Beneficiary ID', r => show(r.BeneficiaryId)],
            ['Created On', r => showDate(r.CreatedOn)],
            ['File', r => r.FilePath ? <Button variant="link" size="sm" className="p-0" onClick={() => TokenPaymentsServices.openFile(r.FilePath)}>View File</Button> : '-'],
            ['Status', r => <Badge variant={r.Status === 'Active' ? 'success' : 'secondary'}>{show(r.Status)}</Badge>],
        ]
    },
    {
        title: 'Owner Settlement Details', key: 'ownerSettlementDetails', columns: [
            ['Settled Amount', r => formatAmount(r.SettledAmount)],
            ['Batch ID', r => show(r.BatchId)],
            ['Merchant Ref ID', r => show(r.MerchantRefId)],
            ['Bank Ref ID', r => show(r.BankRefId)],
            ['UTR No', (r, idx) => renderViewValue('UTR No', r.UtrNo, `utr-${idx}`)],
            ['Settlement Date', r => showDate(r.SettlementDate)],
            ['Settled By', r => show(r.SettlementBy)],
            ['Status', r => <Badge variant="success">{show(r.Status)}</Badge>],
        ]
    },
    {
        title: 'Refund Details', key: 'refundDetails', columns: [
            ['Refund Amount', r => formatAmount(r.RefundAmount)],
            ['Refund Ref ID', r => show(r.RefundRefId)],
            ['Refund Status', r => show(r.RefundStatus)],
            ['Refund Date', r => showDate(r.RefundDate)],
            ['Refund By', r => show(r.RefundBy)],
        ]
    },
];

const TokenPaymentDetails = ({ listingId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!listingId) return;
        const load = async () => {
            setLoading(true);
            const result = await TokenPaymentsServices.getTokenPaymentDetails({ ListingId: listingId });
            setData(result && result.status === 200 ? result.data : null);
            setLoading(false);
        };
        load();
    }, [listingId]);

    if (loading) return <div className="text-center p-3">{constants.LOADING_DATA}</div>;

    const property = data?.propertyDetails?.[0];
    if (!property) return <p className="p-3">{constants.ZERO_DATA}</p>;

    const statusId = Number(property.BookingStatusId) || 1;

    return (
        <div style={{ fontSize: '13px' }}>
            {/* Header */}
            <Card className="mb-3 shadow-sm border-0">
                <Card.Body className="py-3">
                    <Row className="align-items-center">
                        <Col md={5}>
                            <div className="d-flex align-items-center">
                                <div className="mr-3 p-2 bg-light rounded text-center" style={{ width: '40px', height: '40px' }}>
                                    <i className="flaticon2-architecture-and-city text-primary"></i>
                                </div>
                                <div>
                                    <div className="font-weight-bold text-dark">{property.ListingName}</div>
                                    <div className="text-muted small">Listing ID #{property.ListingId}</div>
                                </div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="text-muted small">Owner / Tenant</div>
                            <div className="font-weight-bold text-dark">{show(property.OwnerName)}</div>
                            <div className="text-muted small">{show(property.TenantName)}</div>
                        </Col>
                        <Col md={2}>
                            <div className="text-muted small">Token Amount</div>
                            <div className="font-weight-bold text-dark">{formatAmount(property.TokenAmount)}</div>
                            <div className="text-muted small">Move-in: {show(property.MoveInDate)}</div>
                        </Col>
                        <Col md={2} className="text-md-right">
                            <TokenStatusPill statusId={property.BookingStatusId} text={property.BookingStatus} />
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Payment journey */}
            <Card className="mb-3 shadow-sm border-0">
                <div className="d-flex justify-content-between align-items-center bg-white border-bottom" style={{ padding: '12px 20px' }}>
                    <h6 className="mb-0 font-weight-bold" style={{ fontSize: '14px' }}>Payment Journey</h6>
                    <TokenStatusPill statusId={property.BookingStatusId} text={property.BookingStatus} />
                </div>
                <Card.Body className="p-4 overflow-auto">
                    <div className="d-flex" style={{ minWidth: '640px' }}>
                        {STEPS.map((label, index) => {
                            const stepId = index + 1;
                            const isRefunded = statusId === 5;
                            const status = getStepStatus(stepId, statusId);
                            const t = getStepTheme(status, isRefunded);
                            const nextStatus = stepId < STEPS.length ? getStepStatus(stepId + 1, statusId) : null;

                            // Connecting line: red dashed if skipped, green if next step reached, else grey
                            let line = '3px solid #e2e8f0';
                            if (status === 'skipped' || nextStatus === 'skipped') line = '3px dashed #fca5a5';
                            else if (nextStatus === 'done' || nextStatus === 'current') line = '3px solid #16a34a';

                            let icon = stepId;
                            if (status === 'done' || (status === 'current' && isRefunded)) icon = <i className="flaticon2-check-mark" style={{ fontSize: '13px' }}></i>;
                            else if (status === 'skipped') icon = <i className="flaticon2-cross" style={{ fontSize: '11px' }}></i>;

                            return (
                                <div key={label} className="text-center" style={{ flex: 1, position: 'relative' }}>
                                    {nextStatus && (
                                        <div style={{ position: 'absolute', top: '19px', left: 'calc(50% + 26px)', width: 'calc(100% - 52px)', borderTop: line }}></div>
                                    )}
                                    <div style={{
                                        width: '40px', height: '40px', borderRadius: '50%', margin: '0 auto',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '14px', fontWeight: 700, position: 'relative', zIndex: 1,
                                        background: t.bg, color: t.color,
                                        border: `2px ${status === 'skipped' ? 'dashed' : 'solid'} ${t.border}`,
                                        boxShadow: status === 'current' ? `0 0 0 6px ${isRefunded ? '#dcfce7' : '#dbeafe'}` : 'none'
                                    }}>
                                        {icon}
                                    </div>
                                    <div style={{
                                        marginTop: '14px', fontSize: '12px', fontWeight: status === 'pending' ? 500 : 600,
                                        color: t.label, padding: '0 6px', lineHeight: 1.3,
                                        textDecoration: status === 'skipped' ? 'line-through' : 'none'
                                    }}>
                                        {label}
                                    </div>
                                    <span style={{
                                        display: 'inline-block', marginTop: '6px', padding: '2px 8px', borderRadius: '10px',
                                        fontSize: '10px', fontWeight: 600, letterSpacing: '.3px', textTransform: 'uppercase',
                                        background: t.tagBg, color: t.tagColor
                                    }}>
                                        {t.tag}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </Card.Body>
            </Card>

            {/* Detail tables */}
            {SECTIONS.map(section => {
                const rows = data[section.key] || [];
                return (
                    <Card key={section.key} className="mb-3 shadow-sm">
                        <Card.Header className="bg-white py-2">
                            <h6 className="mb-0">{section.title} <Badge variant="primary">{rows.length}</Badge></h6>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table responsive hover className="mb-0">
                                <thead className="thead-light">
                                    <tr>{section.columns.map(([heading]) => <th key={heading}>{heading}</th>)}</tr>
                                </thead>
                                <tbody>
                                    {rows.length > 0 ? rows.map((row, idx) => (
                                        <tr key={idx}>{section.columns.map(([heading, render]) => <td key={heading}>{render(row, idx)}</td>)}</tr>
                                    )) : (
                                        <tr><td colSpan={section.columns.length} className="text-center text-muted py-3">No records found</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                );
            })}
        </div>
    );
};

export default TokenPaymentDetails;
